import { concentration, costPerVote, type Derived } from './model'
import type { TurnoutRollup } from '../api'
import { rank, route, type Finding, type Severity } from '@/shared/ui/council'

export type { Finding, Severity }

/**
 * The AI Council — patterns in the turnout data, read back as decisions.
 *
 * ## Why this council is deterministic and the Advisory one is not
 *
 * `/advisory` runs real models over a hashed brief, and it is the right tool for a
 * question nobody wrote a rule for. It is the wrong tool for polling day: it costs
 * seconds and money per answer, and on the one afternoon this module exists for, an
 * agent needs the same answer every time they look. So the council here is a rule set
 * over the numbers already on the page — it cannot hallucinate a figure, because every
 * sentence it emits is built from a value it was handed.
 *
 * The escape hatch is deliberate: a question no rule matches says so and offers the
 * Advisory console, rather than inventing an answer.
 *
 * ## Every card must survive the "so what" test
 *
 * A finding that names a number and stops is a chart with extra words. Each card here
 * carries three things a campaign manager can act on before the polls shut: what is
 * true, how many votes it is worth, and the move. `votesAtStake` is what ranks them —
 * not severity words, which are opinions about numbers rather than the numbers.
 */


/** Minutes are the right unit for polling day and useless past it — "493330 min" is noise. */
function since(minutes: number): string {
  if (minutes < 120) return `${minutes} minutes`
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} hours`
  return `${Math.round(minutes / (60 * 24))} days`
}

export function advise(d: Derived, rollup: TurnoutRollup | null): Finding[] {
  const out: Finding[] = []
  const t = d.totals
  const pct = (n: number) => `${Math.round(n * 100)}%`
  const num = (n: number) => Math.round(n).toLocaleString()
  const timeLeft = d.hours.length - (d.hour + 1)

  // ── the shortfall against the clock ────────────────────────────────────────
  // The headline finding. Everything else is a theory of WHERE this comes from.
  const expected = Math.round(t.members * t.paceShare)
  const gap = expected - t.voted
  if (gap > 0 && !d.closed) {
    out.push({
      id: 'pace',
      severity: gap > t.members * 0.1 ? 'critical' : 'serious',
      headline: `You are ${num(gap)} votes behind the clock with ${timeLeft} ${timeLeft === 1 ? 'hour' : 'hours'} of polling left.`,
      evidence: `${num(t.voted)} of ${num(t.members)} members voted (${pct(t.share)}); the hour says you should be at ${pct(t.paceShare)}, or ${num(expected)}.`,
      action: timeLeft > 0
        ? `Every remaining hour has to move ${num(Math.ceil((t.members - t.voted) / Math.max(timeLeft, 1)))} people to finish. Start with the cells listed below rather than a general push — the shortfall is not evenly spread.`
        : 'No hours remain; this is now the result, not a target.',
      votesAtStake: gap,
    })
  } else if (d.closed && t.members > 0) {
    out.push({
      id: 'result',
      severity: 'good',
      headline: `Final: ${pct(t.share)} of your members voted.`,
      evidence: `${num(t.voted)} of ${num(t.members)}. ${num(t.outstanding)} did not.`,
      action: 'Debrief against the cells that finished above 80% — their coordinators are the ones to keep for the next cycle.',
      votesAtStake: 0,
    })
  }

  // ── silence, which is not the same as slowness ─────────────────────────────
  // Only while there is something to do about it. After the close, "ring these
  // coordinators before you move any vehicle" is an instruction about a day that is over,
  // and a card that tells somebody to act on a finished election destroys trust in every
  // other card beside it.
  const silent = d.cells.filter((c) => (c.stalledMinutes ?? 0) >= 90)
  if (silent.length > 0 && !d.closed) {
    const atRisk = silent.reduce((n, c) => n + c.outstanding, 0)
    out.push({
      id: 'silent',
      severity: 'critical',
      headline: `${silent.length} ${silent.length === 1 ? 'cell has' : 'cells have'} gone quiet — you do not know what is happening there.`,
      evidence: `Longest silence ${since(Math.max(...silent.map((c) => c.stalledMinutes ?? 0)))}. Those cells hold ${num(atRisk)} members who have not been recorded as voting.`,
      action: 'Ring these coordinators before you move any vehicle. A silent cell is as likely to be a flat phone as a stalled turnout, and the two need opposite responses.',
      votesAtStake: atRisk,
      cellCodes: silent.slice(0, 6).map((c) => c.code),
    })
  }

  // ── where the shortfall actually lives ─────────────────────────────────────
  const behind = d.cells
    .filter((c) => c.behind > 0.15 && c.outstanding > 0)
    .sort((a, b) => b.outstanding * b.behind - a.outstanding * a.behind)
  if (behind.length > 0 && !d.closed) {
    const top = behind.slice(0, 5)
    const recoverable = top.reduce((n, c) => n + Math.round(c.members * c.behind), 0)
    out.push({
      id: 'behind',
      severity: 'serious',
      headline: `${behind.length} ${behind.length === 1 ? 'cell is' : 'cells are'} more than 15 points behind pace; the worst ${top.length} hold most of it.`,
      evidence: top.map((c) => `${c.code} ${pct(c.share)} vs ${pct(c.paceShare)} (${num(c.outstanding)} left)`).join(' · '),
      action: `Send your floating agents to these five first. Bringing them to pace alone is worth about ${num(recoverable)} votes — more than a uniform push across every cell.`,
      votesAtStake: recoverable,
      cellCodes: top.map((c) => c.code),
    })
  }

  // ── the pledge that never became a vote ────────────────────────────────────
  // The most actionable list on polling day: these people said yes to somebody.
  const pledgeGap = Math.max(t.pledged - t.voted, 0)
  if (pledgeGap > 0 && !d.closed) {
    out.push({
      id: 'pledge-gap',
      severity: pledgeGap > t.members * 0.15 ? 'serious' : 'watch',
      headline: `${num(pledgeGap)} people pledged to vote and have not.`,
      evidence: `${num(t.pledged)} pledges against ${num(t.voted)} recorded votes. Pledge capture is ${t.members > 0 ? pct(t.pledged / t.members) : '0%'} of members.`,
      action: 'This is the highest-yield call list you have — they have already agreed once, so the ask is a reminder rather than a persuasion. Work it before any cold contact.',
      votesAtStake: pledgeGap,
    })
  }

  // ── transport, queues, escalations ─────────────────────────────────────────
  if (t.transportGaps > 0 && !d.closed) {
    out.push({
      id: 'transport',
      severity: t.transportGaps > 20 ? 'serious' : 'watch',
      headline: `${num(t.transportGaps)} members need a lift that has not been matched.`,
      evidence: `Unmatched transport requests across ${d.cells.filter((c) => c.transportGaps > 0).length} cells.`,
      action: 'Transport is the one blocker you can clear with money rather than persuasion. Match vehicles to the cells furthest behind pace, not to the ones that asked first.',
      votesAtStake: t.transportGaps,
      cellCodes: d.cells.filter((c) => c.transportGaps > 0).slice(0, 6).map((c) => c.code),
    })
  }

  if (t.inQueue > 0 && !d.closed) {
    const worst = [...d.stations].sort((a, b) => b.inQueue - a.inQueue)[0]
    if (worst && worst.inQueue > 0) {
      out.push({
        id: 'queue',
        severity: t.inQueue > t.members * 0.08 ? 'serious' : 'watch',
        headline: `${num(t.inQueue)} of your people are standing in a queue right now.`,
        evidence: `Heaviest at ${worst.name} with ${num(worst.inQueue)} waiting.`,
        action: 'These votes are already won unless people leave. Send water and a presiding-officer complaint before you send another van — a queue that empties is a vote lost after you paid for the trip.',
        votesAtStake: t.inQueue,
      })
    }
  }

  if (t.escalationsLate > 0) {
    out.push({
      id: 'escalations',
      severity: 'serious',
      headline: `${t.escalationsLate} escalations are past their due time.`,
      evidence: `${t.escalationsOpen} open in total.`,
      action: 'An escalation nobody closed is a process that has stopped working, and it will read as negligence in any dispute afterwards. Assign each to a named person now.',
      votesAtStake: 0,
    })
  }

  // ── the post-mortem, which only exists once the day is over ────────────────
  // The single most useful thing the platform can tell a campaign after a poll: was the
  // pledge worth anything? If pledgers voted at the same rate as everybody else, the
  // pledge-capture operation cost money and bought nothing, and that is a decision about
  // NEXT cycle rather than about this afternoon.
  if (d.closed && t.pledged > 0 && t.members > t.pledged) {
    const conversion = Math.min(t.voted / t.pledged, 1)
    const nonPledged = t.members - t.pledged
    const votesFromNonPledged = Math.max(t.voted - t.pledged * conversion, 0)
    const baseRate = nonPledged > 0 ? votesFromNonPledged / nonPledged : 0
    out.push({
      id: 'conversion',
      severity: conversion > baseRate + 0.15 ? 'good' : 'watch',
      headline: conversion > baseRate + 0.15
        ? `Pledging worked: pledged members turned out at ${pct(conversion)}.`
        : `Pledging bought little: pledged members turned out at ${pct(conversion)}, against ${pct(baseRate)} for everybody else.`,
      evidence: `${num(t.pledged)} pledges, ${num(t.voted)} votes, ${num(nonPledged)} members who never pledged.`,
      action: conversion > baseRate + 0.15
        ? 'Fund pledge capture again next cycle, and capture earlier — the gap between the two rates is what the operation is worth per member.'
        : 'Before funding pledge capture again, find out whether the pledges were real or whether coordinators were recording agreement that had not been given.',
      votesAtStake: 0,
    })
  }

  // ── structural findings: coverage and command ──────────────────────────────
  if (t.cellsNoCoordinator > 0) {
    const orphan = d.cells.filter((c) => !c.coordinatorName)
    out.push({
      id: 'no-coordinator',
      severity: d.closed ? 'watch' : 'serious',
      headline: `${t.cellsNoCoordinator} ${t.cellsNoCoordinator === 1 ? 'cell has' : 'cells have'} nobody in charge of ${t.cellsNoCoordinator === 1 ? 'it' : 'them'}.`,
      evidence: `Those cells hold ${num(orphan.reduce((n, c) => n + c.members, 0))} members and turned out at ${pct(avgShare(orphan))} against ${pct(avgShare(d.cells.filter((c) => c.coordinatorName)))} for cells that have one.`,
      action: 'A cell without a coordinator is a list, not an organisation. Promote a member on the spot — the gap between the two figures above is what the appointment is worth.',
      votesAtStake: Math.round(orphan.reduce((n, c) => n + c.members, 0)
        * Math.max(avgShare(d.cells.filter((c) => c.coordinatorName)) - avgShare(orphan), 0)),
      cellCodes: orphan.slice(0, 6).map((c) => c.code),
    })
  }

  // ── concentration: is this a broad problem or three bad stations? ──────────
  if (d.stations.length >= 3) {
    const share = concentration(d.stations.map((s) => s.members - s.voted), 3)
    if (share > 0.6) {
      const top = [...d.stations].sort((a, b) => (b.members - b.voted) - (a.members - a.voted)).slice(0, 3)
      out.push({
        id: 'concentration',
        severity: 'watch',
        headline: `${pct(share)} of everything outstanding sits in just three stations.`,
        evidence: top.map((s) => `${s.name} (${num(s.members - s.voted)})`).join(' · '),
        action: 'This is a targeting problem, not a turnout problem. Concentrating your remaining agents and vehicles on these three beats spreading them, and it is a much cheaper afternoon.',
        votesAtStake: top.reduce((n, s) => n + (s.members - s.voted), 0),
      })
    }
  }

  // ── the money ──────────────────────────────────────────────────────────────
  const cpv = costPerVote(rollup, t.voted)
  if (cpv != null) {
    const cur = rollup?.incentiveCurrency ?? 'KES'
    out.push({
      id: 'cost',
      severity: 'watch',
      headline: `Each vote you have recorded has cost ${cur} ${num(cpv)}.`,
      evidence: `${cur} ${num(Number(rollup?.incentiveSpend ?? 0))} of incentive spend against ${num(t.voted)} votes, ${rollup?.incentiveRecipients ?? 0} recipients.`,
      action: `Bringing the outstanding ${num(t.outstanding)} in at the same rate would cost about ${cur} ${num(cpv * t.outstanding)}. Compare that against what the seat is worth before authorising more.`,
      votesAtStake: 0,
    })
  }

  // ── the cells that are working ─────────────────────────────────────────────
  const ahead = d.cells.filter((c) => c.share >= c.paceShare + 0.1 && c.members >= 5)
  if (ahead.length > 0) {
    out.push({
      id: 'ahead',
      severity: 'good',
      headline: `${ahead.length} ${ahead.length === 1 ? 'cell is' : 'cells are'} ahead of pace and ${ahead.length === 1 ? 'needs' : 'need'} nothing from you.`,
      evidence: ahead.slice(0, 5).map((c) => `${c.code} ${pct(c.share)}`).join(' · '),
      action: 'Do not spend anything here. The reason to know this list is so that resources are not sent to cells that were always going to finish.',
      votesAtStake: 0,
      cellCodes: ahead.slice(0, 6).map((c) => c.code),
    })
  }

  return rank(out)
}

function avgShare(cells: { share: number }[]): number {
  if (cells.length === 0) return 0
  return cells.reduce((n, c) => n + c.share, 0) / cells.length
}

// ─── the ask box ─────────────────────────────────────────────────────────────

const ROUTES: { id: string; words: string[] }[] = [
  { id: 'pace', words: ['pace', 'behind', 'clock', 'time', 'target', 'on track', 'winning', 'losing'] },
  { id: 'result', words: ['result', 'final', 'how did', 'outcome', 'end'] },
  { id: 'silent', words: ['silent', 'quiet', 'stall', 'report', 'missing', 'no data', 'dark'] },
  { id: 'behind', words: ['worst', 'weak', 'where', 'focus', 'priorit', 'send', 'agent'] },
  { id: 'pledge-gap', words: ['pledge', 'promise', 'commit', 'call list', 'who to call', 'remind'] },
  { id: 'conversion', words: ['pledge', 'convert', 'worth it', 'did it work', 'next time'] },
  { id: 'transport', words: ['transport', 'van', 'vehicle', 'lift', 'ride', 'boda', 'matatu', 'fuel'] },
  { id: 'queue', words: ['queue', 'line', 'wait', 'crowd', 'slow station'] },
  { id: 'escalations', words: ['escalation', 'incident', 'issue', 'problem', 'complaint'] },
  { id: 'no-coordinator', words: ['coordinator', 'leader', 'in charge', 'captain', 'organis'] },
  { id: 'concentration', words: ['station', 'concentrat', 'spread', 'geograph', 'area', 'ward'] },
  { id: 'cost', words: ['cost', 'money', 'budget', 'spend', 'shilling', 'kes', 'afford', 'worth'] },
  { id: 'ahead', words: ['ahead', 'good', 'best', 'working', 'strong', 'winning cells'] },
]

/**
 * The instant reading, shown beside the advisor's considered one.
 *
 * This used to BE the answer, and returning "no rule covers that" for anything outside the
 * keyword table read as a refusal rather than as the absence of a request. Now it is the fast
 * half of a pair: null simply means the rules have nothing to add, and the card shows only
 * what the council returns.
 */
export function ask(question: string, findings: Finding[]) {
  return route(question, findings, ROUTES)
}
